/**
 * AdminProducts — Specialized form with multi-image upload + video URL field.
 */
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";

const INITIAL = {
  title: "", brand: "", category: "", subcategory: "", price: 0,
  capacity: "", power_hp: 0, weight: "", image: "", images: [], video_url: "",
  description: "", specs: {}, brochure_url: "", gem_url: "", featured: false, visible: true,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/products"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(INITIAL); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...INITIAL, ...it, images: it.images || [], specs: it.specs || {} });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, form);
      else await api.post("/admin/products", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/admin/products/${it.id}`);
    toast.success("Deleted");
    load();
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const setImageAt = (i, v) => { const next = [...(form.images || [])]; next[i] = v; setField("images", next); };
  const removeImageAt = (i) => setField("images", (form.images || []).filter((_, idx) => idx !== i));
  const addImage = () => setField("images", [...(form.images || []), ""]);

  const specsLines = Object.entries(form.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading-xl text-3xl">PRODUCTS</h1>
        <button onClick={startCreate} className="btn-primary text-sm" data-testid="admin-products-new">
          <Plus className="w-4 h-4" /> New Product
        </button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Title", "Brand", "Category", "Price", "Featured", "Visible"].map((c) => (
                <th key={c} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest">{c}</th>
              ))}
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              : items.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No products. Click "New Product".</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`row-${it.id}`}>
                  <td className="px-4 py-3">{it.title}</td>
                  <td className="px-4 py-3">{it.brand}</td>
                  <td className="px-4 py-3">{it.category}</td>
                  <td className="px-4 py-3">{formatINR(it.price)}</td>
                  <td className="px-4 py-3">{it.featured ? "Yes" : "—"}</td>
                  <td className="px-4 py-3">{it.visible ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(it)} className="p-1.5 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">{editing ? "Edit" : "New"} Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input label="Title" value={form.title} onChange={(v) => setField("title", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Brand" value={form.brand} onChange={(v) => setField("brand", v)} />
              <Input label="Category" value={form.category} onChange={(v) => setField("category", v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Subcategory" value={form.subcategory} onChange={(v) => setField("subcategory", v)} />
              <Input label="Price (₹)" type="number" value={form.price} onChange={(v) => setField("price", Number(v) || 0)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input label="Capacity" value={form.capacity} onChange={(v) => setField("capacity", v)} />
              <Input label="Power (HP)" type="number" value={form.power_hp} onChange={(v) => setField("power_hp", Number(v) || 0)} />
              <Input label="Weight" value={form.weight} onChange={(v) => setField("weight", v)} />
            </div>

            <FileUpload value={form.image} onChange={(v) => setField("image", v)} label="Primary Image" accept="image/*" kind="image" />

            {/* Multi-image gallery */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center justify-between">
                <span>Additional Gallery Images</span>
                <button type="button" onClick={addImage} className="text-[#E65C00] hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add Image</button>
              </div>
              <div className="mt-2 space-y-3">
                {(form.images || []).length === 0 && <div className="text-xs text-gray-500 italic">No extra images yet — click "Add Image" to upload multiple shots of this product.</div>}
                {(form.images || []).map((url, i) => (
                  <div key={i} className="flex gap-3 items-start" data-testid={`product-image-row-${i}`}>
                    <div className="flex-1"><FileUpload value={url} onChange={(v) => setImageAt(i, v)} accept="image/*" kind="image" /></div>
                    <button type="button" onClick={() => removeImageAt(i)} className="p-2 text-red-600 hover:bg-red-50" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Video */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Product Video (YouTube URL or upload .mp4)</label>
              <input
                placeholder="https://www.youtube.com/watch?v=... or paste a video URL"
                className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
                value={form.video_url}
                onChange={(e) => setField("video_url", e.target.value)}
                data-testid="product-video-url"
              />
              <div className="mt-2"><FileUpload value={form.video_url && form.video_url.startsWith("/api/uploads") ? form.video_url : ""} onChange={(v) => setField("video_url", v)} accept="video/*,.mp4,.mov,.webm" kind="video" allowUrl={false} label="Or upload a video file" /></div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Description</label>
              <textarea rows={4} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Specifications (one per line, format: Key: Value)</label>
              <textarea rows={6} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-xs font-mono" defaultValue={specsLines}
                onBlur={(e) => {
                  const out = {};
                  e.target.value.split("\n").forEach((l) => { const idx = l.indexOf(":"); if (idx > 0) out[l.slice(0, idx).trim()] = l.slice(idx + 1).trim(); });
                  setField("specs", out);
                }}
              />
            </div>

            <FileUpload value={form.brochure_url} onChange={(v) => setField("brochure_url", v)} label="Brochure (PDF)" accept=".pdf" kind="file" />
            <Input label="GeM Link" value={form.gem_url} onChange={(v) => setField("gem_url", v)} />

            <div className="flex gap-6">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.featured} onChange={(e) => setField("featured", e.target.checked)} /><span className="text-sm font-bold">Featured on homepage</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.visible} onChange={(e) => setField("visible", e.target.checked)} /><span className="text-sm font-bold">Visible on site</span></label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="product-save"><Save className="w-4 h-4" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Input({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</label>
      <input type={type} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
